"use client";

import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { supabase } from '@/utils/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff } from 'lucide-react';
import { FaGoogle, FaGithub } from 'react-icons/fa';
import { showError, showSuccess } from '@/utils/toast';

const Login = () => {
  const { signIn, user, isLoading, profile } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const redirectPath = searchParams.get('redirect');

  useEffect(() => {
    if (!isLoading && user && profile) {
      const target = redirectPath ? decodeURIComponent(redirectPath) : '/dashboard';
      navigate(target, { replace: true });
    }
  }, [user, isLoading, profile, navigate, redirectPath]);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      showError('Email and password are required');
      return;
    }

    setSubmitting(true);
    try {
      await signIn(email, password);
      showSuccess('Login successful!');
    } catch (error: any) {
      console.error('Login error:', error);
      showError(error.message || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOAuthLogin = async (provider: 'github' | 'google') => {
    setSubmitting(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: window.location.origin + (redirectPath ? `?redirect=${encodeURIComponent(redirectPath)}` : ''),
        },
      });

      if (error) throw error;
      showSuccess(`Redirecting to ${provider}...`);
    } catch (error: any) {
      console.error('OAuth login error:', error);
      showError(`Failed to login with ${provider}: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="i-heroicons-arrow-path h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 relative overflow-hidden">
      {/* Subtle Lock Patterns */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml,%3Csvg width=%2240%22 height=%2240%22 viewBox=%220 0 40 40%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cpath d=%22M20 10 V15 M20 15 H25 M20 15 H15 M20 25 V30 M20 30 H25 M20 30 H15%22 stroke=%22%233b82f6%22 stroke-width=%221%22 fill=%22none%22 opacity=%220.1%3E%3C/svg%3E')]"></div>
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl animate-pulse"></div>
      </div>

      <Card className="w-full max-w-md relative z-10">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl">Welcome to WightWay Connect</CardTitle>
          <CardDescription>Sign in to your account to continue</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <form onSubmit={handleEmailLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12 px-4 py-3 text-base" // Increased height and padding for better usability
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 px-4 py-3 text-base pr-12" // Increased height, padding, and right padding for icon
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full h-12 text-base" disabled={submitting || !email || !password}>
              {submitting ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          {/* OAuth Buttons - Same icons as sign up page */}
          <div className="space-y-3">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground text-center mb-2">
              Or continue with
            </div>
            <div className="space-y-2">
              <Button 
                variant="outline" 
                type="button"
                onClick={() => handleOAuthLogin('google')}
                disabled={submitting}
                className="w-full h-12 text-base"
              >
                <FaGoogle className="h-4 w-4 mr-2" />
                Continue with Google
              </Button>
              <Button 
                variant="outline" 
                type="button"
                onClick={() => handleOAuthLogin('github')}
                disabled={submitting}
                className="w-full h-12 text-base"
              >
                <FaGithub className="h-4 w-4 mr-2" />
                Continue with GitHub
              </Button>
            </div>
          </div>

          <div className="text-center text-sm text-muted-foreground">
            <Link to="/forgot-password" className="text-primary hover:underline">
              Forgot your password?
            </Link>
          </div>
          <div className="text-center text-sm text-muted-foreground">
            Don't have an account? <Link to="/signup" className="text-primary hover:underline">Sign up</Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;